import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { currencyFormatter, dateFormatter } from 'core/utils/commonUtils';
import { OrderStateEnum } from 'models/enums/orderStateEnum';
import type { Order } from 'models/order/order';
import type { FC } from 'react';

const OrderCard: FC<{
  order: Order;
  onClick: () => void;
  getActionInfo: (order: Order) => { name: string; color: string } | null;
  ordersType: OrderStateEnum;
}> = ({ order, onClick, getActionInfo, ordersType }) => {
  const statusInfo = getActionInfo(order);

  // Определяем дату для отображения в зависимости от типа заказа
  const getDisplayDate = (): Date => {
    if (ordersType === OrderStateEnum.Request) {
      return order.dateCreate;
    } else if (ordersType === OrderStateEnum.Order) {
      return order.dateChangeStateToOrder ?? order.dateCreate;
    }
    return order.dateChangeStateToOrder ?? order.dateCreate;
  };

  return (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
        },
        '&:focus-within': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
      }}
      onClick={onClick}
      role='button'
      tabIndex={0}
      aria-label={`Заказ номер ${order.number}, клиент ${order.client.fullName}, статус ${
        statusInfo?.name ?? 'Не задан'
      }`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}>
      <CardContent sx={{ pb: '16px !important' }}>
        {/* Основная информация */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}>
          <Typography variant='h6' component='h3' sx={{ fontWeight: 600 }}>
            № {order.number}
          </Typography>
          <Chip
            label={statusInfo?.name ?? 'Не задан'}
            sx={{
              borderColor: statusInfo?.color ?? grey[500],
              color: statusInfo?.color ?? grey[500],
            }}
            size='small'
            variant='outlined'
            aria-label={`Статус: ${statusInfo?.name ?? 'Не задан'}`}
          />
        </Box>

        {/* Клиент */}
        <Typography variant='body1' sx={{ mb: 1, fontWeight: 500 }}>
          {order.client.fullName}
        </Typography>

        {/* Дата создания */}
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {dateFormatter.format(getDisplayDate())}
        </Typography>

        {/* Детали в аккордеоне */}
        <Accordion
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              minHeight: 'auto',
              px: 0,
              '& .MuiAccordionSummary-content': {
                my: 1,
              },
            },
            '& .MuiAccordionDetails-root': {
              px: 0,
              pt: 0,
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`order-${order.id}-details`}
            id={`order-${order.id}-summary`}>
            <Typography variant='body2' color='primary'>
              Подробнее
            </Typography>
          </AccordionSummary>
          <AccordionDetails id={`order-${order.id}-details`}>
            <List dense sx={{ py: 0 }}>
              {/* Адрес */}
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText
                  primary='Адрес'
                  secondary={order.address || 'Не указан'}
                  slotProps={{
                    primary: { variant: 'caption', color: 'text.secondary' },
                    secondary: { variant: 'body2' },
                  }}
                />
              </ListItem>

              {/* Сумма платежей */}
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText
                  primary='Сумма платежей'
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {order.sumPayments >= order.sum ? (
                        <Chip
                          label={currencyFormatter.format(order.sumPayments)}
                          color='success'
                          size='small'
                          variant='outlined'
                        />
                      ) : (
                        <Typography variant='body2'>
                          {currencyFormatter.format(order.sumPayments)}
                        </Typography>
                      )}
                    </Box>
                  }
                  slotProps={{
                    primary: { variant: 'caption', color: 'text.secondary' },
                  }}
                />
              </ListItem>

              {/* Общая сумма */}
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemText
                  primary='Общая сумма'
                  secondary={currencyFormatter.format(order.sum)}
                  slotProps={{
                    primary: { variant: 'caption', color: 'text.secondary' },
                    secondary: { variant: 'body2' },
                  }}
                />
              </ListItem>

              {/* Примечание */}
              {order.remark && (
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary='Примечание'
                    secondary={order.remark}
                    slotProps={{
                      primary: { variant: 'caption', color: 'text.secondary' },
                      secondary: { variant: 'body2' },
                    }}
                  />
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default OrderCard;
